import axios from 'axios';
import { Socket } from 'socket.io-client';

// Add this at the top of the file for Vite env types
/// <reference types="vite/client" />

// Extend ImportMeta interface for Vite env
interface ImportMetaEnv {
  readonly VITE_BACKEND_URL: string;
}

interface ImportMeta {
  readonly env: ImportMetaEnv;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isThinking?: boolean;
  codeBlocks?: Array<{
    language: string;
    code: string;
  }>;
}

export interface ChatResponse {
  message: Message;
  metadata?: {
    latency: number;
    source: string;
    fallback_used: boolean;
  };
  next_action?: string;
  debug_info?: any;
}

export interface ClarificationPrompt {
  response: string;
  options: Array<{ label: string; value: string }>;
  originalMessage: string;
}

export class ChatService {
  private socket: Socket | null;
  private baseUrl: string;
  private sessionId: string;

  constructor(socket: Socket | null, sessionId: string) {
    this.socket = socket;
    this.baseUrl = (import.meta as any)?.env?.VITE_BACKEND_URL || '';
    this.sessionId = sessionId;
  }

  async sendMessage(
    message: string,
    activeTools: any,
    onStream: (chunk: any) => void,
    onClarification: (prompt: ClarificationPrompt) => void,
    onError: (error: any) => void,
    onThinking: (status: any) => void,
    useEnhancedMode: boolean = true
  ): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/api/ai/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: message }],
          sessionId: this.sessionId,
          activeTools,
          enhanced_mode: useEnhancedMode  // Enable enhanced ALORA Intelligence by default
        })
      });

      if (!response.ok) throw new Error('Failed to send message');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let clarificationBuffer: any = null;

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                return;
              }

              try {
                const parsed = JSON.parse(data);
                
                // Handle different message types for Enhanced ALORA
                if (parsed.type === 'thinking') {
                  onThinking({
                    message: parsed.message,
                    status: parsed.status
                  });
                } else if (parsed.type === 'content') {
                  onStream({
                    content: parsed.content,
                    type: 'content',
                    agent: parsed.agent,
                    metadata: parsed.metadata
                  });
                } else if (parsed.type === 'complete') {
                  onStream({
                    type: 'complete',
                    agent: parsed.agent,
                    data: parsed.data,
                    enhanced_mode: parsed.enhanced_mode
                  });
                } else if (parsed.error) {
                  onError({ message: parsed.error });
                  return;
                } else {
                  // Legacy format support - pass through all fields from the backend chunk as-is
                  onStream(parsed);
                }

                // Handle clarification prompt (legacy)
                if (parsed.state === 'awaiting_clarification' && parsed.clarification) {
                  clarificationBuffer = parsed;
                  onClarification({
                    response: parsed.response,
                    options: parsed.clarification.options,
                    originalMessage: message
                  });
                }

              } catch (e) {
                console.error('Failed to parse chunk:', e);
              }
            }
          }
        }
      }
    } catch (error) {
      onError(error);
    }
  }

  async sendClarification(
    message: string,
    clarificationResponse: string
  ): Promise<ChatResponse> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/agents/grc-agent`,
        {
          message,
          session_id: this.sessionId,
          clarification_response: clarificationResponse
        }
      );

      return {
        message: {
          id: Date.now().toString(),
          role: 'assistant',
          content: response.data.response || response.data.message || '',
          timestamp: new Date()
        }
      };
    } catch (error) {
      throw new Error('Failed to send clarification response');
    }
  }

  async analyzePR(owner: string, repo: string, prNumber: number): Promise<any> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/api/github/analyze-pr/${owner}/${repo}/${prNumber}`
      );
      return response.data;
    } catch (error) {
      throw new Error('Failed to analyze PR');
    }
  }

  async searchMemory(query: string, namespace: string = 'default'): Promise<any> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/pinecone/query`,
        {
          query,
          topK: 5,
          namespace,
          includeMetadata: true
        }
      );
      return response.data.matches;
    } catch (error) {
      throw new Error('Failed to search memory');
    }
  }

  async storeMemory(
    content: string,
    metadata: any,
    namespace: string = 'default'
  ): Promise<void> {
    try {
      await axios.post(
        `${this.baseUrl}/api/pinecone/store`,
        {
          content,
          metadata: {
            ...metadata,
            sessionId: this.sessionId,
            timestamp: new Date().toISOString()
          },
          namespace
        }
      );
    } catch (error) {
      throw new Error('Failed to store memory');
    }
  }

  async getChatHistory(): Promise<Message[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/api/ai/chat/${this.sessionId}`);
      return response.data.messages.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
    } catch (error) {
      console.error('Failed to fetch chat history:', error);
      throw new Error('Failed to fetch chat history');
    }
  }
} 